// MedicationEventHistory.jsx
import React, { useState } from "react";
import { Paper, Typography, Box, IconButton, Tooltip } from "@mui/material";
import { Cancel, CheckCircle, Info, Medication } from "@mui/icons-material";
import MonthChange from "./MonthChange";
import { format } from "date-fns";
import { useParams } from "react-router-dom";
import { useQuery } from "react-query";
import { QK } from "../../../../../base/qk";
import { getMedicationEvents } from "../../../../../api/services/medication";
import Loader from "../../../../Loader/Loader";
import EventMedicationRow from "./EventMedicationRow";
import NotFound from "../../../../NotFound/NotFound";
import { useSnackbar } from "notistack";
import useClientDetails from "../../../../../hooks/useClientDetails";
import ReactDOMServer from "react-dom/server";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPrint } from "@fortawesome/free-solid-svg-icons";
import MadEventPrint from "./MadEventPrint";

const MedicationEventHistory = () => {
  const { id: clientID } = useParams();
  const [selectedMonth, setSelectedMonth] = useState(
    format(new Date(), "yyyy-MM")
  );
  const { enqueueSnackbar } = useSnackbar();
  const [daysInMonth, setDaysInMonth] = useState(28);

  const handleMonthChange = (month, days) => {
    setSelectedMonth(month);
    setDaysInMonth(days);
  };

  const { data: medEvents, isLoading: medEventLoading } = useQuery(
    [QK.medicationEvents, clientID, selectedMonth],
    () => getMedicationEvents(clientID, selectedMonth),
    { enabled: !!clientID || !!selectedMonth }
  );

  const { fullName, NhsNumber, birthDate, areaName, clientDetails } =
    useClientDetails(clientID);

  const handlePrint = async () => {
    try {
      const freshData = await getMedicationEvents(clientID, selectedMonth);

      const printWindow = window.open("");
      printWindow.document.write(`
        <html>
          <head>
            <title>${fullName ? fullName + "'s Medication Chart" : "Medication Chart"}</title>
            <style>
              @media print {
                body {
                  font-family: Arial, sans-serif;
                  margin: 15px;
                  color: #000;
                }
                table {
                  width: 100%;
                  border-collapse: collapse;
                  font-size: 12px;
                }
                th, td {
                  border: 1px solid #000;
                  padding: 6px;
                  text-align: left;
                }
                th {
                  background-color: #f2f2f2;
                }
                @page {
                  size: A4 landscape;
                  margin: 1cm;
                }
              }
            </style>
          </head>
          <body>
            ${ReactDOMServer.renderToString(
              <MadEventPrint
                medications={freshData}
                selectedMonth={selectedMonth}
                fullName={fullName}
                birthDate={birthDate}
                NhsNumber={NhsNumber}
                areaName={areaName}
                clientDetails={clientDetails}
              />
            )}
          </body>
        </html>
      `);
      printWindow.document.close();

      setTimeout(() => {
        printWindow.print();
      }, 500);
    } catch (error) {
      console.error("Error printing medications:", error);
      enqueueSnackbar("Failed to fetch data for printing", {
        variant: "error",
      });
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        padding: 2,
        borderRadius: 2,
        border: "1px solid #E2E8F0",
        width: "100%",
      }}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={1.5}
        flexWrap="wrap"
        gap={1.5}
      >
        <Typography
          variant="body1"
          fontWeight={700}
          fontSize="0.95rem"
          display="flex"
          alignItems="center"
          color="#1A1A2E"
        >
          <Medication
            sx={{ fontSize: 22, marginRight: 0.75, color: "#2469F0" }}
          />
          Medications Event History
          <Tooltip
            title={
              <Box sx={{ p: 0.5 }}>
                <Typography
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    fontSize: "0.75rem",
                  }}
                >
                  <CheckCircle sx={{ color: "#4caf50", fontSize: 14 }} /> Given
                </Typography>
                <Typography
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    fontSize: "0.75rem",
                  }}
                >
                  <Cancel sx={{ color: "#f44336", fontSize: 14 }} /> Not Given
                </Typography>
                <Typography
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    fontSize: "0.75rem",
                  }}
                >
                  <CheckCircle sx={{ color: "#eab308", fontSize: 14 }} /> Low
                  Stock
                </Typography>
                <Typography
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    fontSize: "0.75rem",
                  }}
                >
                  <Medication sx={{ color: "#9e9e9e", fontSize: 14 }} /> No Data
                </Typography>
              </Box>
            }
          >
            <IconButton size="small" sx={{ ml: 0.5, p: 0.5 }}>
              <Info sx={{ fontSize: 16, color: "#64748B" }} />
            </IconButton>
          </Tooltip>
        </Typography>

        <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
          <MonthChange onMonthChange={handleMonthChange} />
          <FontAwesomeIcon
            className="text-danger cursor-pointer"
            size="lg"
            icon={faPrint}
            onClick={handlePrint}
          />
        </Box>
      </Box>

      <Box sx={{ overflowX: "auto", pb: 1, width: "100%" }}>
        <Box
          sx={{
            display: "flex",
            padding: 0.75,
            marginBottom: 1.5,
            bgcolor: "#F8FAFC",
            borderRadius: 1.5,
            border: "1px solid #E2E8F0",
            width: "100%",
          }}
        >
          <Box sx={{ width: "340px", flexShrink: 0 }}></Box>
          <Box
            sx={{
              flex: 1,
              display: "flex",
              justifyContent: "space-between",
              pr: 1,
            }}
          >
            {Array.from({ length: daysInMonth }, (_, i) => (
              <Box
                key={i}
                sx={{
                  flex: 1,
                  textAlign: "center",
                  fontWeight: 700,
                  fontSize: "0.7rem",
                  color: "#475569",
                  minWidth: 0,
                }}
              >
                {i + 1}
              </Box>
            ))}
          </Box>
        </Box>

        {medEventLoading && <Loader />}
        {medEvents?.length === 0 && <NotFound message="No Medicine Found" />}
        {medEvents?.map((med, index) => (
          <EventMedicationRow
            key={index}
            med={med}
            selectedMonth={selectedMonth}
            daysInMonth={daysInMonth}
          />
        ))}
      </Box>
    </Paper>
  );
};

export default MedicationEventHistory;